import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Home, Loader2, LogOut, KeyRound, Hourglass, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CURRENCIES } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function Onboarding() {
  const navigate = useNavigate();
  const { user, pending, loadHousehold, logout } = useAuth();
  const [name, setName] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const createHousehold = async () => {
    if (!name.trim()) return toast.error("Enter a household name");
    setBusy(true);
    try {
      await api.post("/households", { name: name.trim(), currency });
      await loadHousehold();
      toast.success("Household created! You're the admin.");
      navigate("/dashboard", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create household");
    } finally {
      setBusy(false);
    }
  };

  const joinByCode = async () => {
    if (!code.trim()) return toast.error("Enter your invite code");
    setBusy(true);
    try {
      const { data } = await api.post("/households/join", { code: code.trim().toUpperCase() });
      await loadHousehold();
      toast.success(`Request sent to ${data.household_name}. The admin needs to approve you.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not join");
    } finally {
      setBusy(false);
    }
  };

  const checkStatus = async () => {
    setBusy(true);
    await loadHousehold();
    setBusy(false);
  };

  const cancelRequest = async () => {
    setBusy(true);
    try {
      await api.post("/households/join/cancel");
      await loadHousehold();
      toast.success("Join request cancelled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not cancel");
    } finally {
      setBusy(false);
    }
  };

  if (pending) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center px-5 py-10">
        <div className="w-full max-w-md rounded-3xl border border-border bg-card p-6 text-center animate-rise" data-testid="join-pending-card">
          <div className="h-14 w-14 rounded-2xl bg-amber-100 text-amber-600 dark:bg-amber-500/15 mx-auto flex items-center justify-center mb-4"><Hourglass className="h-7 w-7" /></div>
          <h2 className="font-heading font-bold text-xl">Waiting for approval</h2>
          <p className="text-sm text-muted-foreground mt-2">Your request to join <span className="font-semibold text-foreground" data-testid="pending-household-name">{pending.household_name}</span> was sent. Once the household admin approves you, you'll see the dashboard and start sharing expenses.</p>
          <Button onClick={checkStatus} disabled={busy} className="w-full mt-5 h-11 rounded-xl" data-testid="check-approval-btn">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-2" /> Check again</>}
          </Button>
          <Button onClick={cancelRequest} disabled={busy} variant="ghost" className="w-full mt-2 h-10 rounded-xl text-muted-foreground" data-testid="cancel-join-request-btn">Cancel request</Button>
          <button onClick={logout} className="mx-auto mt-4 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="pending-logout"><LogOut className="h-3.5 w-3.5" /> Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-5 py-10">
      <div className="w-full max-w-md animate-rise">
        <div className="flex items-center gap-3 mb-8">
          <div className="h-11 w-11 rounded-2xl bg-primary flex items-center justify-center text-primary-foreground font-heading font-bold text-xl">R</div>
          <div>
            <div className="font-heading font-bold text-lg">Welcome, {user?.name?.split(" ")[0]}</div>
            <div className="text-xs text-muted-foreground">Let's set up your household</div>
          </div>
        </div>

        <div className="rounded-3xl border border-border bg-card p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-1">
            <Home className="h-5 w-5 text-primary" />
            <h2 className="font-heading font-bold text-lg">Create a household</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">You'll become the Household Admin and can add roommates by email.</p>
          <Label htmlFor="hh-name" className="text-xs">Household name</Label>
          <Input id="hh-name" data-testid="household-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Maple Street Apt 4B" className="mt-1.5 h-11 rounded-xl" />
          <Label className="text-xs mt-4 block">Currency (based on your region)</Label>
          <Select value={currency} onValueChange={setCurrency}>
            <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="household-currency-select"><SelectValue /></SelectTrigger>
            <SelectContent className="max-h-72">
              {CURRENCIES.map((c) => <SelectItem key={c.code} value={c.code} data-testid={`currency-option-${c.code}`}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={createHousehold} disabled={busy} data-testid="create-household-btn" className="w-full mt-4 h-11 rounded-xl font-semibold">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create household"}
          </Button>
        </div>

        <div className="rounded-3xl border border-border bg-secondary/40 p-6 mt-4">
          <div className="flex items-center gap-2 mb-1">
            <KeyRound className="h-5 w-5 text-muted-foreground" />
            <h3 className="font-heading font-semibold">Have an invite code?</h3>
          </div>
          <p className="text-sm text-muted-foreground mb-3">Enter the code from your invitation email to join your household.</p>
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="e.g. 7K4PQ2" maxLength={8} className="h-12 rounded-xl font-mono tracking-[0.4em] text-center text-lg uppercase" data-testid="invite-code-input" />
          <Button onClick={joinByCode} disabled={busy} variant="outline" data-testid="join-household-btn" className="w-full mt-3 h-11 rounded-xl">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-2" /> Join household</>}
          </Button>
        </div>

        <button onClick={logout} className="mx-auto mt-6 flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="onboarding-logout">
          <LogOut className="h-3.5 w-3.5" /> Sign out
        </button>
      </div>
    </div>
  );
}
